/**
 * Sistema BBCode modular y extensible
 * Permite añadir nuevos formatos fácilmente
 */

import DOMPurify from 'dompurify'
import { uniqueHeadingSlug } from './headingSlug.js'
class BBCodeParser {
  constructor() {
    this.tags = new Map()
    this._usedHeadingSlugs = new Set()
    this.initializeDefaultTags()
  }

  /**
   * Registra un nuevo tag BBCode
   * @param {string} name - Nombre del tag (ej: 'b', 'i', 'url')
   * @param {Object} config - Configuración del tag
   */
  registerTag(name, config) {
    this.tags.set(name.toLowerCase(), {
      openTag: config.openTag || `<${name}>`,
      closeTag: config.closeTag || `</${name}>`,
      selfClosing: config.selfClosing || false,
      hasAttribute: config.hasAttribute || false,
      attributeName: config.attributeName || null,
      transform: config.transform || null,
      validate: config.validate || (() => true),
      priority: config.priority || 0,
    })
  }

  /**
   * Inicializa los tags por defecto
   */
  initializeDefaultTags() {
    // Tags básicos de formato
    this.registerTag('b', {
      openTag: '<strong>',
      closeTag: '</strong>',
      priority: 1,
    })

    this.registerTag('i', {
      openTag: '<em>',
      closeTag: '</em>',
      priority: 1,
    })

    this.registerTag('u', {
      openTag: '<u>',
      closeTag: '</u>',
      priority: 1,
    })

    this.registerTag('s', {
      openTag: '<del>',
      closeTag: '</del>',
      priority: 1,
    })

    // Tags con atributos
    this.registerTag('color', {
      hasAttribute: true,
      attributeName: 'color',
      transform: (content, attribute) => {
        const color = this.sanitizeColor(attribute)
        return `<span style="color: ${color}">${content}</span>`
      },
      validate: (attribute) => this.isValidColor(attribute),
      priority: 2,
    })

    this.registerTag('size', {
      hasAttribute: true,
      attributeName: 'size',
      transform: (content, attribute) => {
        const size = this.sanitizeSize(attribute)
        return `<span style="font-size: ${size}">${content}</span>`
      },
      validate: (attribute) => this.isValidSize(attribute),
      priority: 2,
    })

    this.registerTag('url', {
      hasAttribute: true,
      attributeName: 'url',
      transform: (content, attribute) => {
        const url = this.sanitizeUrl(attribute || content)
        const linkText = attribute ? content : url
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${linkText}</a>`
      },
      validate: (attribute, content) => this.isValidUrl(attribute || content),
      priority: 3,
    })

    // Tags de estructura
    this.registerTag('quote', {
      hasAttribute: true,
      attributeName: 'author',
      transform: (content, attribute) => {
        const author = attribute ? `<cite>— ${this.escapeHtml(attribute)}</cite>` : ''
        return `<blockquote class="bbcode-quote">${content}${author}</blockquote>`
      },
      priority: 4,
    })

    this.registerTag('ooc', {
      hasAttribute: true,
      attributeName: 'note',
      transform: (content, attribute) => {
        const note = this.escapeHtml(attribute || 'OOC')
        return `<span class="bbcode-ooc" tabindex="0" data-ooc-note="${note}"><span class="bbcode-ooc__text">${content}</span><span class="bbcode-ooc__tip" role="tooltip">${note}</span></span>`
      },
      validate: (attribute) => !!attribute && String(attribute).trim().length > 0,
      priority: 2,
    })

    this.registerTag('code', {
      hasAttribute: true,
      attributeName: 'lang',
      transform: (content, attribute) => {
        const lang = attribute ? ` class="language-${this.escapeHtml(attribute)}"` : ''
        return `<pre><code${lang}>${this.escapeHtml(content)}</code></pre>`
      },
      priority: 5,
    })

    // Tags de lista
    this.registerTag('list', {
      hasAttribute: true,
      attributeName: 'type',
      transform: (content, attribute) => {
        const listType = attribute === '1' ? 'ol' : 'ul'
        const processedContent = content.replace(/\[\*\]/g, '<li>').replace(/\n\s*(?=<li>)/g, '')
        return `<${listType} class="bbcode-list">${processedContent}</li></${listType}>`
      },
      priority: 6,
    })

    // Tags multimedia
    this.registerTag('img', {
      hasAttribute: true,
      attributeName: 'alt',
      selfClosing: true,
      transform: (content, attribute) => {
        const url = this.sanitizeUrl(content)
        const alt = attribute ? this.escapeHtml(attribute) : 'Imagen'
        return `<img src="${url}" alt="${alt}" class="bbcode-img" loading="lazy">`
      },
      validate: (attribute, content) => this.isValidImageUrl(content),
      priority: 7,
    })

    this.registerTag('youtube', {
      selfClosing: true,
      transform: (content) => {
        const videoId = this.extractYouTubeId(content)
        if (!videoId) return this.escapeHtml(content)
        return `<div class="bbcode-youtube">
          <iframe width="560" height="315"
            src="https://www.youtube.com/embed/${videoId}"
            title="YouTube video player"
            frameborder="0"
            loading="lazy"
            referrerpolicy="no-referrer"
            sandbox="allow-same-origin allow-scripts"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen>
          </iframe>
        </div>`
      },
      validate: (attribute, content) => this.isValidYouTubeUrl(content),
      priority: 8,
    })

    // Tags especiales
    this.registerTag('spoiler', {
      hasAttribute: true,
      attributeName: 'title',
      transform: (content, attribute) => {
        const title = attribute ? this.escapeHtml(attribute) : 'Spoiler'
        return `<details class="bbcode-spoiler">
          <summary>${title}</summary>
          <div class="spoiler-content">${content}</div>
        </details>`
      },
      priority: 9,
    })

    this.registerTag('center', {
      openTag: '<div style="text-align: center;">',
      closeTag: '</div>',
      priority: 3,
    })

    this.registerTag('right', {
      openTag: '<div style="text-align: right;">',
      closeTag: '</div>',
      priority: 3,
    })

    this.registerTag('h2', {
      transform: (content) => {
        const id = uniqueHeadingSlug(content, this._usedHeadingSlugs)
        return `<h2 id="${id}" class="bbcode-h2">${content}</h2>`
      },
      priority: 4,
    })

    this.registerTag('h3', {
      transform: (content) => {
        const id = uniqueHeadingSlug(content, this._usedHeadingSlugs)
        return `<h3 id="${id}" class="bbcode-h3">${content}</h3>`
      },
      priority: 4,
    })
  }

  /**
   * Convierte BBCode a HTML
   * @param {string} bbcode - Texto con BBCode
   * @returns {string} HTML resultante
   */
  parse(bbcode) {
    if (!bbcode || typeof bbcode !== 'string') {
      return ''
    }

    this._usedHeadingSlugs = new Set()

    let html = bbcode

    // Escapar HTML existente
    html = this.escapeHtml(html)

    // Procesar saltos de línea
    html = html.replace(/\r\n/g, '\n').replace(/\r/g, '\n')

    // Ordenar tags por prioridad
    const sortedTags = Array.from(this.tags.entries()).sort(([, a], [, b]) => a.priority - b.priority)

    // Procesar cada tag
    for (const [tagName, config] of sortedTags) {
      html = this.processTag(html, tagName, config)
    }

    // Convertir saltos de línea a <br>
    html = html.replace(/\n/g, '<br>')

    // Sanitizar HTML final con DOMPurify (permitiendo tags/attrs necesarios)
    const domPurifyConfig = {
      ADD_TAGS: ['iframe', 'details', 'summary'],
      ADD_ATTR: [
        'style',
        'class',
        'target',
        'rel',
        'loading',
        'referrerpolicy',
        'sandbox',
        'allow',
        'allowfullscreen',
        'width',
        'height',
        'src',
        'href',
        'title',
        'alt',
        'id',
        'frameborder',
        'tabindex',
        'data-ooc-note',
        'role',
      ]
    }

    // Evitar fallos en SSR/tests (sin window/document)
    if (typeof window !== 'undefined' && DOMPurify && typeof DOMPurify.sanitize === 'function') {
      return DOMPurify.sanitize(html, domPurifyConfig)
    }

    // Entorno sin DOM: devolver HTML escapado (ya escapado arriba)
    return html
  }

  /**
   * Procesa un tag específico
   */
  processTag(html, tagName, config) {
    if (config.selfClosing) {
      return this.processSelfClosingTag(html, tagName, config)
    } else {
      return this.processRegularTag(html, tagName, config)
    }
  }

  /**
   * Procesa tags auto-cerrados
   */
  processSelfClosingTag(html, tagName, config) {
    const regex = config.hasAttribute
      ? new RegExp(`\\[${tagName}(?:=([^\\]]+))?\\]([^\\[]+)\\[\\/${tagName}\\]`, 'gi')
      : new RegExp(`\\[${tagName}\\]([^\\[]+)\\[\\/${tagName}\\]`, 'gi')

    return html.replace(regex, (match, attribute, content) => {
      if (config.hasAttribute) {
        content = content || attribute
        attribute = attribute && content !== attribute ? attribute : null
      } else {
        content = attribute
        attribute = null
      }

      if (config.validate && !config.validate(attribute, content)) {
        return match
      }

      if (config.transform) {
        return config.transform(content, attribute)
      }

      return config.openTag + content + config.closeTag
    })
  }

  /**
   * Procesa tags regulares
   */
  processRegularTag(html, tagName, config) {
    const regex = config.hasAttribute
      ? new RegExp(`\\[${tagName}(?:=([^\\]]+))?\\]([\\s\\S]*?)\\[\\/${tagName}\\]`, 'gi')
      : new RegExp(`\\[${tagName}\\]([\\s\\S]*?)\\[\\/${tagName}\\]`, 'gi')

    return html.replace(regex, (match, attribute, content) => {
      if (!config.hasAttribute) {
        content = attribute
        attribute = null
      }

      if (config.validate && !config.validate(attribute, content)) {
        return match
      }

      if (config.transform) {
        return config.transform(content, attribute)
      }

      return config.openTag + content + config.closeTag
    })
  }

  // Métodos de validación y sanitización
  isValidColor(color) {
    if (!color) return false
    const colorRegex = /^(#[0-9A-Fa-f]{3,6}|[a-zA-Z]+|rgb\([^)]+\)|rgba\([^)]+\))$/
    return colorRegex.test(color)
  }

  isValidSize(size) {
    if (!size) return false
    const sizeRegex = /^(\d+(?:\.\d+)?(px|em|rem|%|pt))$/
    return sizeRegex.test(size) || /^(xx-small|x-small|small|medium|large|x-large|xx-large)$/.test(size)
  }

  isValidUrl(url) {
    if (!url) return false
    try {
      new URL(url)
      return true
    } catch {
      return /^https?:\/\//.test(url)
    }
  }

  isValidImageUrl(url) {
    if (!this.isValidUrl(url)) return false
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url)
  }

  isValidYouTubeUrl(url) {
    if (!url) return false
    return /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/.test(url)
  }

  extractYouTubeId(url) {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/)
    return match ? match[1] : null
  }

  sanitizeColor(color) {
    if (!color) return 'inherit'
    // Lista de colores seguros
    const safeColors = [
      'black',
      'white',
      'red',
      'green',
      'blue',
      'yellow',
      'orange',
      'purple',
      'pink',
      'brown',
      'gray',
      'grey',
      'cyan',
      'magenta',
      'lime',
      'navy',
      'maroon',
      'olive',
      'teal',
      'silver',
      'aqua',
      'fuchsia',
    ]

    if (safeColors.includes(color.toLowerCase())) {
      return color.toLowerCase()
    }

    if (/^#[0-9A-Fa-f]{3,6}$/.test(color)) {
      return color
    }

    if (/^rgb\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*\)$/.test(color)) {
      return color
    }

    if (/^rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*[01]?\.?\d*\s*\)$/.test(color)) {
      return color
    }

    return 'inherit'
  }

  sanitizeSize(size) {
    if (!size) return '1em'

    if (/^(xx-small|x-small|small|medium|large|x-large|xx-large)$/.test(size)) {
      return size
    }

    if (/^\d+(?:\.\d+)?(px|em|rem|%|pt)$/.test(size)) {
      const numericValue = parseFloat(size)
      const unit = size.replace(/[\d.]/g, '')

      // Limitar tamaños extremos
      if (unit === 'px' && (numericValue < 8 || numericValue > 72)) {
        return '14px'
      }
      if (unit === 'em' && (numericValue < 0.5 || numericValue > 5)) {
        return '1em'
      }
      if (unit === '%' && (numericValue < 50 || numericValue > 300)) {
        return '100%'
      }

      return size
    }

    return '1em'
  }

  sanitizeUrl(url) {
    if (!url) return '#'

    // Permitir URLs relativas
    if (url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) {
      return url
    }

    // Asegurar protocolo para URLs absolutas
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = 'https://' + url
    }

    try {
      const urlObj = new URL(url)
      // Solo permitir protocolos seguros
      if (['http:', 'https:'].includes(urlObj.protocol)) {
        return urlObj.href
      }
    } catch {
      // URL inválida
    }

    return '#'
  }

  escapeHtml(text) {
    if (!text) return ''

    // Verificar si estamos en el navegador
    if (typeof document !== 'undefined') {
      const div = document.createElement('div')
      div.textContent = text
      return div.innerHTML
    }

    // Fallback para entornos sin DOM
    return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }

  /**
   * Obtiene la lista de tags disponibles
   * @returns {Array} Lista de nombres de tags
   */
  getAvailableTags() {
    return Array.from(this.tags.keys())
  }

  /**
   * Obtiene información sobre un tag específico
   * @param {string} tagName - Nombre del tag
   * @returns {Object|null} Información del tag
   */
  getTagInfo(tagName) {
    return this.tags.get(tagName.toLowerCase()) || null
  }

  /**
   * Elimina un tag del parser
   * @param {string} tagName - Nombre del tag a eliminar
   */
  removeTag(tagName) {
    this.tags.delete(tagName.toLowerCase())
  }

  /**
   * Limpia todos los tags personalizados (mantiene los por defecto)
   */
  resetToDefaults() {
    this.tags.clear()
    this.initializeDefaultTags()
  }
}

export default BBCodeParser
